import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Sun, ArrowRight, Loader2, Rss, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { AudioBriefPlayer, BriefPlaylist } from "@/components/AudioBriefPlayer";
import { PageLoader, useMinLoading } from "@/components/Preloader";

const briefDate = (iso?: string) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

const FEED_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/podcast.xml`;

export default function BriefArchive() {
  const [pages, setPages] = useState(1);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["brief-archive", pages],
    queryFn: async () => (await api.get(`/articles?content_type=market_brief&limit=${pages * 15}`)).data,
    placeholderData: keepPreviousData,
  });
  const showLoader = useMinLoading(isLoading);
  if (showLoader) return <PageLoader />;

  const items: (Article & { key_takeaways?: string[]; audio_url?: string })[] = data?.items || [];
  const hasMore = (data?.total || 0) > items.length;
  const weekTracks = items.filter((b) => b.audio_url).slice(0, 7)
    .map((b) => ({ src: b.audio_url as string, title: b.title, slug: b.slug }));

  return (
    <div data-testid="brief-archive-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-[#7C3AED]/20 text-[#7C3AED]">
          <Sun className="h-5 w-5" />
        </span>
        <h1 className="font-news font-black text-white text-3xl sm:text-4xl">Daily Market Brief</h1>
      </div>
      <p className="text-gray-400 max-w-2xl mb-12">
        Every morning, NewsLayer's intelligence desk distills the last 24 hours of crypto, Web3 and
        policy news into five punchy bullets. This is the full archive.
      </p>

      <BriefPlaylist tracks={weekTracks} />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 -mt-8 mb-12" data-testid="podcast-subscribe">
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
          Also a podcast — subscribe anywhere:
        </span>
        <a href={FEED_URL} target="_blank" rel="noreferrer" data-testid="podcast-feed-link"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[#A78BFA] hover:text-white transition-colors">
          <Rss className="h-3.5 w-3.5" /> RSS feed
        </a>
        <button type="button" data-testid="podcast-copy-button"
          onClick={() => {
            navigator.clipboard?.writeText(FEED_URL)
              .then(() => toast.success("Feed URL copied — paste it into Apple Podcasts, Spotify or any podcast app"))
              .catch(() => toast.info(`Copy this feed URL: ${FEED_URL}`));
          }}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[#A78BFA] hover:text-white transition-colors">
          <Copy className="h-3.5 w-3.5" /> Copy feed URL
        </button>
      </div>

      {items.length === 0 && (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-xl">
          <p className="text-gray-400">The first morning brief drops tomorrow at 7am ET — check back then.</p>
        </div>
      )}

      <ol className="relative border-l-2 border-[#7C3AED]/30 ml-2 sm:ml-4">
        {items.map((b, i) => (
          <li key={b.id} data-testid={`brief-timeline-item-${i}`} className="relative pl-8 sm:pl-10 pb-12 last:pb-0">
            <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-[#7C3AED] ring-4 ring-[#7C3AED]/20" />
            <p className="font-mono text-[11px] uppercase tracking-widest text-[#7C3AED] mb-2">
              {briefDate(b.published_at)} · {timeAgo(b.published_at)}
            </p>
            <Link to={`/article/${b.slug}`} className="group block">
              <h2 className="font-news font-bold text-white text-xl sm:text-2xl leading-snug group-hover:text-[#00E5FF] transition-colors">
                {b.title}
              </h2>
            </Link>
            {b.audio_url && (
              <div className="mt-4 max-w-xl" data-testid={`brief-audio-${i}`}>
                <AudioBriefPlayer src={b.audio_url} title={b.title} sponsor={b.audio_sponsor} slug={b.slug} />
              </div>
            )}
            {(b.key_takeaways || []).length > 0 && (
              <ul className="mt-4 space-y-2.5">
                {(b.key_takeaways || []).map((t, j) => (
                  <li key={j} className="flex gap-3 text-sm text-gray-300 leading-relaxed">
                    <span className="font-news font-black text-[#7C3AED] shrink-0">{j + 1}.</span>
                    {t}
                  </li>
                ))}
              </ul>
            )}
            <Link to={`/article/${b.slug}`} data-testid={`brief-read-link-${i}`}
              className="inline-flex items-center gap-1 text-sm text-[#00E5FF] font-medium mt-4 hover:gap-2 transition-all">
              Read the full brief <ArrowRight className="h-4 w-4" />
            </Link>
          </li>
        ))}
      </ol>

      {hasMore && (
        <div className="text-center mt-12">
          <button data-testid="brief-load-more" onClick={() => setPages((p) => p + 1)} disabled={isFetching}
            className="inline-flex items-center gap-2 border border-[#7C3AED]/60 hover:border-[#7C3AED] text-white font-medium rounded-md px-6 py-2.5 transition-colors disabled:opacity-50">
            {isFetching && <Loader2 className="h-4 w-4 animate-spin" />} Load older briefs
          </button>
        </div>
      )}
    </div>
  );
}
